import { useState } from "react";

import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import { TRANSCRIPTION_STATUS } from "@/constants";
import EditMentionModal from "@/containers/EditMentionModal";
import { useEpisode } from "@/containers/Episode/context";
import TranscribeEpisode from "@/containers/Episode/partials/TranscribeEpisode";
import FullTranscriptModal from "@/containers/FullTranscriptModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useEpisodes } from "@/contexts/EpisodesContext";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useUser } from "@/hooks/useUser";
import { Mention } from "@/types/Mention";
import { User } from "@/types/User";
import { copyToClipboard } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSubContent,
  DropdownSubMenu,
  DropdownSubTrigger,
  DropdownToggle,
} from "@components/Dropdown";
import StaffBadge from "@components/StaffBadge";
import TextButton from "@components/TextButton";

import { MentionSentiment } from "../../Sentiment";

type ActionProps = {
  mention?: Mention;
  setEditing?: (editing: boolean) => void;
};

const Actions: React.FunctionComponent<ActionProps> = ({
  mention,
  setEditing,
}) => {
  const { data: user } = useUser();
  const { is_staff = false, is_readonly = true } = (user?.data as User) || {};
  const [fullTranscriptOpen, setFullTranscriptOpen] = useState(false);
  const [editMentionOpen, setEditMentionOpen] = useState(false);
  const episode = useEpisode();
  const { danger, success } = useMessages();
  const { updateMention } = useEpisodes();
  const {
    episode_id,
    episode_has_transcript,
    episode_transcript_status,
    updateMentionSentiment,
  } = episode;

  const transcriptionInProgress =
    episode_transcript_status === TRANSCRIPTION_STATUS.QUEUED;

  const canShowFullTranscripts = useHasFeature(
    FEATURE.HAS_SHOW_FULL_TRANSCRIPT_FEATURE
  );

  // Early return for readonly - since they wouldn't see anything anyway
  if (is_readonly) return null;

  // Check what the user has right to see
  const showFullTranscript =
    !transcriptionInProgress &&
    episode_has_transcript &&
    canShowFullTranscripts;

  const showEditMention = mention?.context === "text" && Boolean(setEditing);
  const showEditSentiment = mention && episode.mentions.length > 1;

  // Perform an early return if the user is going to see an empty menu
  const menuAppearsEmpty =
    is_staff === false &&
    !showFullTranscript &&
    !showEditMention &&
    !showEditSentiment;

  if (menuAppearsEmpty) {
    return null;
  }

  return (
    <>
      <Dropdown>
        <DropdownToggle>
          <TextButton
            icon={DotsHorizontal}
            data-testid="mention-action-menu-toggler"
          />
        </DropdownToggle>

        <DropdownMenu>
          {showFullTranscript && (
            <DropdownItem
              data-testid="full-transcript-button"
              onClick={() => setFullTranscriptOpen(true)}
            >
              {L.MENTIONS_SHOW_FULL_TRANSCRIPT}
            </DropdownItem>
          )}

          {showEditMention && (
            <DropdownItem
              data-testid="edit-mention"
              onClick={() => (setEditing ? setEditing(true) : null)}
            >
              {L.MENTIONS_EDIT_MENTION}
            </DropdownItem>
          )}
          <TranscribeEpisode
            component={DropdownItem}
            data-testid="request-transcript"
          />
          <AccessControl level={ACCESS_LEVELS.STAFF}>
            <DropdownItem
              href={`/internal/channels/episode/${episode_id}`}
              target="_blank"
            >
              <StaffBadge />
              {L.EPISODE_VIEW_IN_ADMIN}
            </DropdownItem>
          </AccessControl>
          {(mention || episode.mentions[0]) && (
            <>
              <AccessControl level={ACCESS_LEVELS.STAFF}>
                <DropdownItem
                  href={mention?.admin_url || episode.mentions[0].admin_url}
                  target="_blank"
                  data-testid="mention-admin-url"
                >
                  <StaffBadge />
                  {L.MENTIONS_VIEW_IN_ADMIN}
                </DropdownItem>
              </AccessControl>

              <AccessControl level={ACCESS_LEVELS.STAFF}>
                <DropdownItem
                  onClick={async () => {
                    try {
                      await copyToClipboard(
                        `${window.location.origin}${window.location.pathname}${
                          mention?.public_id || episode.mentions?.[0].public_id
                        }`
                      );
                      success({
                        title: L.SHARE_MODAL_COPY_URL_SUCCESS,
                      });
                    } catch {
                      danger({
                        title: L.MENTIONS_SHARE_COPY_TO_CLIPBOARD_FAILED,
                      });
                    }
                  }}
                >
                  <StaffBadge />
                  {L.MENTIONS_ANCHOR_LINK}
                </DropdownItem>
              </AccessControl>
            </>
          )}
          {/** News Episodes _only_ support aggregated mentions, so if there's no specific mention passed in. Don't show this */}
          {showEditSentiment && (
            <DropdownSubMenu>
              <DropdownSubTrigger data-testid="edit-sentiment-dropdown">
                {L.MENTIONS_SENTIMENT_EDIT_TONE}
              </DropdownSubTrigger>
              <DropdownSubContent>
                <MentionSentiment
                  value={mention.sentiment}
                  onChange={(newSentiment) => {
                    updateMentionSentiment(mention, newSentiment);
                  }}
                />
              </DropdownSubContent>
            </DropdownSubMenu>
          )}
        </DropdownMenu>
      </Dropdown>

      <FullTranscriptModal
        show={fullTranscriptOpen}
        onHide={() => setFullTranscriptOpen(false)}
        episode={episode}
      />
      {mention?.content ? (
        <EditMentionModal
          show={editMentionOpen}
          onHide={() => setEditMentionOpen(false)}
          mention={mention}
          updateMention={updateMention}
        />
      ) : null}
    </>
  );
};

export default Actions;
